// inv/src/pages/authentication/Billing.jsx
import { useState } from 'react';
import Pagination from '../../components/ui/pagination';
import InvoiceDownloadButton from './InvoiceDownloadButton';
import { MEMBERSHIP_NAMES } from '../../config';
import { useMembershipAccess } from '../../hooks/membership';

const Billing = ({ user }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const membership = useMembershipAccess({ user, loading: false });

    if (!user) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Billing Information
                </h2>
                <p className="text-gray-600">Loading user data…</p>
            </div>
        );
    }

    const displayName =
        MEMBERSHIP_NAMES[membership.rawMembershipType] || MEMBERSHIP_NAMES.freeMembership;
    const itemsPerPage = 5;

    const transactions = user.payments || [];
    const totalPages = Math.ceil(transactions.length / itemsPerPage);
    const startIdx = (currentPage - 1) * itemsPerPage;
    const currentTransactions = transactions.slice(
        startIdx,
        startIdx + itemsPerPage
    );

    const getPaymentStatus = (tx) => {
        return tx.status ? tx.status : 'Unverified status';
    };

    const formatAmount = (cents) =>
        (Math.abs(cents) / 100).toFixed(2);

    const formatDate = (date) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(date).toLocaleDateString('en-US', options);
    };

    // helpers  ────────────────────────────────────────────────
    const getTransactionType = (amountCents) =>
        amountCents < 0 ? 'Credit' : 'Charge';

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Billing Information
            </h2>

            <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
                {/* Transactions List */}
                <div className="w-full md:w-1/2">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                        Transaction history
                    </h3>

                    {transactions.length === 0 ? (
                        <p className="text-gray-600">No transactions yet.</p>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {currentTransactions.map((tx) => (
                                    <div
                                        key={tx.transaction_id ?? tx.id}
                                        className="h-32 px-4 bg-stone-50 rounded-lg shadow flex justify-between items-center"
                                    >
                                        {/* Left: ID + Date */}
                                        <div>
                                            <p className="text-gray-800 text-xs font-bold m-0">
                                                ID:{' '}
                                                <span className="text-gray-600 font-normal">
                                                    {tx.transaction_id ?? tx.id}
                                                </span>
                                            </p>
                                            <p className="mt-2 text-gray-800 text-xs font-bold m-0">
                                                Date:{' '}
                                                <span className="text-gray-600 font-normal">
                                                    {formatDate(tx.date)}
                                                </span>
                                            </p>
                                        </div>

                                        {/* Right: Amount, Status, Download */}
                                        <div className="text-right">
                                            <p className="text-gray-800 text-xs font-bold m-0">
                                                {getTransactionType(tx.amount)}:{' '}
                                                <span className="text-gray-600 font-normal">
                                                    ${formatAmount(tx.amount)}
                                                </span>
                                            </p>
                                            <p
                                                className={`text-xs font-bold m-0 mt-1 ${
                                                    getPaymentStatus(tx) === 'completed'
                                                        ? 'text-green-600'
                                                        : 'text-yellow-500'
                                                }`}
                                            >
                                                Status:{' '}
                                                <span className="font-normal">
                                                    {getPaymentStatus(tx) === 'completed'
                                                        ? 'Completed'
                                                        : 'Unverified status'}
                                                </span>
                                            </p>
                                            <div className="mt-2">
                                                <InvoiceDownloadButton transactionId={tx.transaction_id} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="mt-6">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                        useUrl={false}
                                        className="justify-center"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Membership Info */}
                <div className="w-full md:w-1/2">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                        Membership Information
                    </h3>
                    <div className="h-32 flex flex-col justify-center items-start py-4 px-4 bg-stone-50 rounded-lg shadow">
                        <p className="text-md text-gray-800 font-semibold m-0">
                            Current Membership:{' '}
                            <span className="font-normal">
                                {displayName}
                            </span>
                        </p>
                        <p className="text-md text-gray-600 m-0 mt-2">
                            Next Payment Date:{' '}
                            <span className="font-normal">
                                {user.nextBillingDate
                                    ? formatDate(user.nextBillingDate)
                                    : '—'}
                            </span>
                        </p>
                        <p
                            className={`text-md font-medium mt-2 ${
                                membership.isActive ? 'text-green-600' : 'text-red-500'
                            }`}
                        >
                            Status:{' '}
                            <span className="font-normal">
                                {membership.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Billing;
